import { Controller } from "@hotwired/stimulus";

// Matrix-mode picker for bulk contact-group membership edits.
//
// Each cell carries:
//   data-group-id
//   data-contactable-id      — row identity (Profile::* / Location / PointOfSale row id)
//   data-initial-member      "true"|"false" — server-authoritative membership at load
//   data-current-member      "true"|"false" — user's pending intent (mutated on click)
//
// The contactable_type is a property of the entire picker session (single-type
// picker) and is sent via a hidden form field — not per cell.
//
// Visual classes are computed on every paint from initial + current:
//   initial=T current=T  -> idle member (filled dot)
//   initial=F current=F  -> idle non-member (empty dot)
//   initial=F current=T  -> pending add (green +)
//   initial=T current=F  -> pending remove (red -)
//
// On submit we emit one hidden input per changed cell:
//   initial=F current=T  -> <input name="add_pairs[]"    value="gid:cid">
//   initial=T current=F  -> <input name="remove_pairs[]" value="gid:cid">
// Unchanged cells produce nothing — server treats absence as no-op.
export default class extends Controller {
  static targets = ["cell", "columnHeader", "outputs", "summary"];

  connect() {
    this.cellTargets.forEach((cell) => this.paint(cell));
    this.updateSummary();
  }

  toggleCell(event) {
    const cell = event.currentTarget;
    const next = cell.dataset.currentMember !== "true";
    cell.dataset.currentMember = next ? "true" : "false";
    cell.setAttribute("aria-pressed", next ? "true" : "false");
    this.paint(cell);
    this.updateSummary();
  }

  // Column header acts as "flip the column toward the minority state".
  // If any cell in the column currently shows non-member, the click makes
  // every cell a member. Otherwise it makes every cell a non-member. This
  // matches the ergonomic of the old per-group checkbox: one click = "add
  // everyone" / another click = "remove everyone".
  toggleColumn(event) {
    const groupId = event.currentTarget.dataset.groupId;
    if (!groupId) return;

    const cells = this.cellTargets.filter((c) => c.dataset.groupId === groupId);
    if (cells.length === 0) return;

    const anyNonMember = cells.some((c) => c.dataset.currentMember !== "true");
    const nextValue = anyNonMember ? "true" : "false";

    cells.forEach((c) => {
      c.dataset.currentMember = nextValue;
      c.setAttribute("aria-pressed", nextValue);
      this.paint(c);
    });
    this.updateSummary();
  }

  onSubmit() {
    if (!this.hasOutputsTarget) return;
    this.outputsTarget.replaceChildren();

    this.cellTargets.forEach((cell) => {
      const initial = cell.dataset.initialMember === "true";
      const current = cell.dataset.currentMember === "true";
      if (initial === current) return;

      const gid = cell.dataset.groupId;
      const cid = cell.dataset.contactableId;
      if (!gid || !cid) return;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = current ? "add_pairs[]" : "remove_pairs[]";
      input.value = `${gid}:${cid}`;
      this.outputsTarget.appendChild(input);
    });
  }

  // --- private ---------------------------------------------------------

  paint(cell) {
    const initial = cell.dataset.initialMember === "true";
    const current = cell.dataset.currentMember === "true";
    const icon = cell.querySelector(
      '[data-contact-group-picker-target="cellIcon"]',
    );
    if (!icon) return;

    // Reset every style we might apply.
    icon.className =
      "flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold";
    icon.textContent = "";

    if (initial && current) {
      // Idle member.
      icon.classList.add("bg-default-100", "text-default-700");
      icon.textContent = "●";
    } else if (!initial && !current) {
      // Idle non-member.
      icon.classList.add(
        "border",
        "border-gray-200",
        "text-transparent",
        "bg-white",
      );
      icon.textContent = "·";
    } else if (!initial && current) {
      // Pending add.
      icon.classList.add("bg-emerald-500", "text-white");
      icon.textContent = "+";
    } else {
      // Pending remove.
      icon.classList.add("bg-red-500", "text-white");
      icon.textContent = "−";
    }
  }

  updateSummary() {
    if (!this.hasSummaryTarget) return;

    let adds = 0;
    let removes = 0;
    this.cellTargets.forEach((cell) => {
      const initial = cell.dataset.initialMember === "true";
      const current = cell.dataset.currentMember === "true";
      if (initial === current) return;
      if (current) adds += 1;
      else removes += 1;
    });

    if (adds === 0 && removes === 0) {
      this.summaryTarget.textContent = "Aucun changement";
      this.summaryTarget.classList.remove("text-gray-800", "font-medium");
      this.summaryTarget.classList.add("text-gray-500");
      return;
    }

    const parts = [];
    if (adds > 0) parts.push(`${adds} ajout${adds > 1 ? "s" : ""}`);
    if (removes > 0) parts.push(`${removes} retrait${removes > 1 ? "s" : ""}`);
    this.summaryTarget.textContent = parts.join(", ");
    this.summaryTarget.classList.remove("text-gray-500");
    this.summaryTarget.classList.add("text-gray-800", "font-medium");
  }
}
